import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

/**
 * Reveal Lines
 *
 * Divide el texto de los elementos .reveal-lines en líneas usando SplitText
 * y anima con yPercent (de abajo hacia arriba, efecto cropped por el padre
 * con overflow:hidden / .masking-text).
 *
 * Modos (data-mode):
 *  - "scroll" (default): animación sincronizada con scrub
 *  - "trigger": animación se reproduce una vez al entrar en viewport
 *
 * Data-attributes opcionales:
 *  - data-mode="scroll|trigger"
 *  - data-start (default: "top 85%")
 *  - data-end (default: "top 50%") — solo en modo scroll
 *  - data-scrub (default: 2) — solo en modo scroll
 *  - data-stagger (default: 0.1)
 *  - data-duration (default: 0.8) — solo en modo trigger
 *  - data-delay (default: 0) — solo en modo trigger
 *  - data-toggle (default: "play none none none") — solo en modo trigger
 */
export function revealLines() {
  const elements = document.querySelectorAll('.reveal-lines')
  if (!elements.length) return

  elements.forEach((el) => {
    const text = new SplitText(el, { type: 'lines', linesClass: 'split-line' })

    // Ocultar el elemento original inmediatamente para evitar flash de contenido
    el.style.opacity = '1'

    // Envolver cada línea en un contenedor con overflow:hidden para el crop
    text.lines.forEach((line) => {
      const wrapper = document.createElement('div')
      wrapper.style.overflow = 'hidden'
      line.parentNode.insertBefore(wrapper, line)
      wrapper.appendChild(line)
    })

    const mode       = el.dataset.mode || 'scroll'
    const stagger    = parseFloat(el.dataset.stagger) || 0.1
    const startVal   = el.dataset.start || 'top 85%'

    // Estado inicial: líneas desplazadas abajo (ocultas por overflow:hidden)
    gsap.set(text.lines, { yPercent: 100 })

    if (mode === 'scroll') {
      const endVal     = el.dataset.end || 'top 50%'
      const scrubValue = parseFloat(el.dataset.scrub) || 2

      gsap.to(text.lines, {
        yPercent: 0,
        stagger,
        ease: 'power1.out',
        scrollTrigger: {
          trigger: el,
          start: startVal,
          end: endVal,
          scrub: scrubValue,
        }
      })
    } else {
      const duration = parseFloat(el.dataset.duration) || 1
      const delay    = parseFloat(el.dataset.delay) || 0
      const toggleActions = el.dataset.toggle || 'play none none none'

      gsap.to(text.lines, {
        yPercent: 0,
        duration,
        delay,
        stagger,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: startVal,
          toggleActions,
        }
      })
    }
  })
}

export function clipBox() {
  const boxes = document.querySelectorAll('.clip-box')
  if (!boxes.length) return

  const clipClosed = 'polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%)'
  const clipOpen = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'

  // Estado por defecto para todos los .clip-box al inicio
  gsap.set(boxes, {
    translateY: "3rem",
    clipPath: clipClosed,
    webkitClipPath: clipClosed,
    willChange: 'clip-path'
  })

  boxes.forEach((box) => {
    const start = box.dataset.start || 'top 50%'
    const end = box.dataset.end || 'bottom 15%'
    const duration = parseFloat(box.dataset.duration) || 1.5
    const ease = box.dataset.ease || 'power4.out'
    const image = box.querySelector('img')

    if (image) {
      gsap.set(image, {
        scale: 1,
        transformOrigin: '50% 50%',
        willChange: 'transform'
      })
    }

    const tl = gsap.timeline({ paused: true })

    tl.to(box, {
      translateY: "0",
      clipPath: clipOpen,
      webkitClipPath: clipOpen,
      duration,
      ease,
      overwrite: 'auto'
    }, 0)

    if (image) {
      tl.to(image, {
        scale: 1.2,
        duration,
        ease,
        overwrite: 'auto'
      }, 0)
    }

    ScrollTrigger.create({
      trigger: box,
      start,
      end,
      invalidateOnRefresh: true,
      onEnter: () => tl.play(),
      onEnterBack: () => tl.play(),
      onLeave: () => tl.reverse(),
      onLeaveBack: () => tl.reverse()
    })
  })
}

export function clipLine() {
  const lines = document.querySelectorAll('.clip-line')
  if (!lines.length) return

  // Sintaxis normalizada para evitar conflictos al interpolar polygon() en GSAP
  const clipClosed = 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)'
  const clipOpen = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'

  gsap.set(lines, {
    clipPath: clipClosed,
    webkitClipPath: clipClosed,
    willChange: 'clip-path'
  })

  lines.forEach((line) => {
    const start = line.dataset.start || 'top 50%'
    const end = line.dataset.end || 'bottom 15%'
    const duration = parseFloat(line.dataset.duration) || 1.5
    const ease = line.dataset.ease || 'power4.out'
    const toggleActions = line.dataset.toggle || 'play none none none'

    const tl = gsap.timeline({ paused: true })

    tl.to(line, {
      clipPath: clipOpen,
      webkitClipPath: clipOpen,
      duration,
      ease,
      overwrite: 'auto'
    }, 0)

    ScrollTrigger.create({
      trigger: line,
      start,
      end,
      animation: tl,
      toggleActions,
      invalidateOnRefresh: true
    })
  })
}

